import { captureMessage } from "@web-monorepo/telemetry";
import Emitter from "../emitter";
import { Adapter, Message, MessageHandler, RealtimeMessage } from "./types";

const ALLOWED_HOSTS = ["classdojo.com", "classdojo.dev", "classdojo.test"];

/**
 * True for a serialized https origin on classdojo.com/.dev/.test, or a subdomain of one.
 *
 * Deliberately not a regex. The predecessor here was an unanchored
 * `/classdojo\.(dev|test|com)/`, which accepted `classdojo.com.evil.io`; parsing with
 * `URL` and comparing structured fields makes that class of mistake unrepresentable,
 * because `endsWith("." + allowed)` is anchored by construction. IDN/punycode
 * normalization, case folding, and port/userinfo/path separation come from the
 * platform parser instead of having to be remembered in a pattern.
 *
 * Local dev runs on `*.classdojo.test` over https, so no localhost exception is needed.
 */
function isAllowedOrigin(origin: string): boolean {
  let url: URL;
  try {
    url = new URL(origin);
  } catch (error) {
    // `new URL` throws TypeError for anything unparseable, which is a legitimate
    // "not an allowed origin" answer. Anything else is unexpected and should surface.
    if (error instanceof TypeError) return false;
    throw error;
  }

  if (url.protocol !== "https:") return false;
  // A serialized origin carries no credentials, path, query or fragment. Anything that
  // does is a different URL wearing an allowed host somewhere inside it. `pathname` is
  // "/" for a bare origin, so both "/" and "" are legitimate here.
  if (url.username !== "" || url.password !== "") return false;
  if (url.search !== "" || url.hash !== "") return false;
  if (url.pathname !== "/" && url.pathname !== "") return false;

  const host = url.hostname;
  return ALLOWED_HOSTS.some((allowed) => host === allowed || host.endsWith(`.${allowed}`));
}

enum Protocol {
  HAND,
  SHAKE,
}

type RealtimeEnvelope = { protocol: Protocol } | { channel: string; message: RealtimeMessage; protocol?: null };

function isOptionalString(value: unknown): boolean {
  return value === undefined || typeof value === "string";
}

function isRealtimeEnvelope(data: unknown): data is RealtimeEnvelope {
  if (!data || typeof data !== "object") return false;
  if ("protocol" in data && data.protocol != null) {
    return data.protocol === Protocol.HAND || data.protocol === Protocol.SHAKE;
  }
  if (!("channel" in data) || typeof data.channel !== "string" || !("message" in data)) return false;
  return isRealtimeMessage(data.message);
}

function isRealtimeMessage(message: unknown): message is RealtimeMessage {
  if (!message || typeof message !== "object" || !("sender" in message) || typeof message.sender !== "string") {
    return false;
  }
  return (
    "payload" in message &&
    (!("topic" in message) || isOptionalString(message.topic)) &&
    (!("command" in message) || isOptionalString(message.command)) &&
    (!("action" in message) || isOptionalString(message.action))
  );
}

export default class IframeAdapter implements Adapter {
  static HANDSHAKE_INTERVAL = 500;

  private _sourceWindow: Window;
  private _targetWindow: Window;
  private _targetOrigin: string | undefined;
  private _emitter: Emitter<Parameters<MessageHandler>>;
  private _handshake: boolean;
  private _handshakeTimedOut: boolean;
  private _handshakeAttempts: number;

  // used for testing only
  private _mock: boolean;

  private _handshaking: ReturnType<typeof setInterval>;

  constructor(
    sourceWindow: Window,
    targetWindow: Window,
    targetOrigin?: string,
    mock?: boolean,
    report = captureMessage,
  ) {
    this._sourceWindow = sourceWindow;
    this._targetWindow = targetWindow;
    this._targetOrigin = targetOrigin;

    // postMessage silently discards the message when targetOrigin doesn't match the
    // target window's real origin, which otherwise surfaces only as a handshake
    // timeout ~21s later. A malformed value is the realistic misconfiguration (a bad
    // `parentOrigin` query param), so report it at construction.
    //
    // captureMessage comes from @web-monorepo/telemetry rather than @sentry/browser
    // because this runs in a constructor that can execute before Telemetry.init():
    // the telemetry export is a queuing shim until init rebinds it, so an early call
    // is buffered and flushed instead of dropped.
    if (targetOrigin != null && !isAllowedOrigin(targetOrigin)) {
      report(
        `IframeAdapter: targetOrigin ${JSON.stringify(targetOrigin)} is not an accepted classdojo origin. ` +
          `postMessage will silently drop every publish to this target.`,
        "warning",
      );
    }
    this._emitter = new Emitter<Parameters<MessageHandler>>();
    this._handshake = false;

    this._handshakeTimedOut = false;

    // this is only relevant for testing
    this._mock = mock ?? false;

    this._handshakeAttempts = 0;
    this._handshaking = setInterval(() => {
      if (this._handshakeAttempts > 40) {
        this._handshakeTimedOut = true;
        clearInterval(this._handshaking);
      } else {
        this._directPublish({ protocol: Protocol.HAND });
        this._handshakeAttempts++;
      }
    }, IframeAdapter.HANDSHAKE_INTERVAL);

    this._sourceWindow.addEventListener("message", (event: MessageEvent<unknown>) => {
      if (!isAllowedOrigin(event.origin) || event.source !== this._targetWindow || !isRealtimeEnvelope(event.data)) {
        return;
      }
      if ("protocol" in event.data && event.data.protocol != null) {
        switch (event.data.protocol) {
          case Protocol.HAND:
            this._directPublish({ protocol: Protocol.SHAKE });
            break;
          case Protocol.SHAKE:
            clearInterval(this._handshaking);
            this._handshake = true;
            break;
        }
      } else {
        this._emitter.emit(event.data.channel, event.data.message);
      }
    });
  }

  private _directPublish(data: Record<string, unknown>): void {
    if (this._mock) {
      // this is only used for tests therefore the type casting
      this._targetWindow.postMessage(data, this._targetOrigin?.length ? this._targetOrigin : "*", [
        this._sourceWindow as unknown as Transferable,
      ]);
    } else {
      // some browsers (e.g. Safari), dislike "*" as the targetOrigin, so provide actual targetOrigin
      // when possible
      this._targetWindow.postMessage(data, this._targetOrigin?.length ? this._targetOrigin : "*");
    }
  }

  publish(channel: string, message: Message) {
    if (this._handshake) {
      this._directPublish({ channel, message });
      return Promise.resolve();
    }
    return new Promise<void>((resolve, reject) => {
      const check = setInterval(() => {
        if (this._handshake) {
          clearInterval(check);
          this._directPublish({ channel, message });
          return resolve();
        }
        if (this._handshakeTimedOut) {
          clearInterval(check);

          return reject(
            new IframeError(
              `Iframe adapter timedout on hand shake. Attempting to send ${JSON.stringify(
                message,
              )}.\n\nChannel: ${channel}\n\nTarget Origin: ${this._targetOrigin}`,
              this._targetOrigin,
            ),
          );
        }
      }, IframeAdapter.HANDSHAKE_INTERVAL);
    });
  }

  subscribe(channel: string, onMessage: MessageHandler) {
    if (!this._emitter.listeners(channel).includes(onMessage)) {
      this._emitter.addListener(channel, onMessage);
    }
  }

  unsubscribe(channel: string, onMessage: MessageHandler) {
    this._emitter.removeListener(channel, onMessage);
  }

  close() {
    this._emitter.removeAllListeners();
  }
}

export class IframeError extends Error {
  public targetOrigin?: string;
  constructor(message: string, targetOrigin?: string) {
    super(message);
    this.targetOrigin = targetOrigin;
  }
}
